import { Combobox as BaseCombobox, ComboboxItemState } from '@base-ui-components/react/combobox';
import clsx from 'clsx';
import { ForwardedRef, forwardRef, HTMLProps, KeyboardEvent, ReactElement, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useUniqueId } from '../../hooks/use-unique-id';
import { refHandler } from '../../utils';
import { Icon } from '../icon';
import { IconButton, IconButtonProps } from '../icon-button';
import { InputWrapper, InputWrapperProps } from '../input-wrapper';
import { OptionGroup } from '../option-group';
import { OptionItem } from '../option-item';
import { Tag } from '../tag';
import { TagList } from '../tag-list';
import classes from './combobox.module.scss';
import { ComboboxOption } from './types.ts';
import { Typography } from '../typography/index.ts';

export type MultiComboboxProps<T extends string | number = string, D = unknown> = {
  /**
   * Represents a list of combobox options.
   * Each item in the array corresponds to an option available in the combobox.
   * Typically used to populate the options of a dropdown menu or similar UI component.
   */
  items: ComboboxOption<T, D>[];

  /**
   * An optional placeholder text that provides a hint or descriptive text
   * to be displayed inside an input field or similar UI element when it is empty.
   * It helps users understand what kind of input is expected.
   */
  placeholder?: string;

  /**
   * An optional string value that can be used to store or represent textual information.
   * If not provided, the value will be undefined.
   */
  value?: T[];

  /**
   * Callback function triggered when the selected value changes.
   */
  onChange?: (value: ComboboxOption<T, D>[]) => void;

  /**
   * Add the option to create new dedicated items
   */
  onCreate?: (query: string) => ComboboxOption<T, D> | void;

  /**
   * Make input clearable
   */
  clearable?: boolean;

  /**
   * A callback function for rendering a custom option in a combobox component.
   */
  renderOption?: (data: {
    option: ComboboxOption<T, D>;
    state: ComboboxItemState;
    props: Omit<HTMLProps<unknown>, 'prefix' | 'color' | 'as'>;
  }) => ReactElement;

} & Omit<InputWrapperProps<T>, 'ref' | 'onChange' | 'value' | 'multiple'>
  & Omit<HTMLProps<HTMLDivElement>, 'ref' | 'label' | 'name' | 'disabled' | 'value' | 'prefix' | 'suffix' | 'onChange' | 'multiple'>;

/**
 * A component that provides a multi-select combobox UI allowing users to select multiple options
 * from a given set of items. It includes features such as input for filtering options, removing
 * selected items, and handling disabled states.
 */
const MultiComboboxInner = <T extends string | number = string, D = unknown>({
  items,
  value,
  onChange,
  placeholder,
  disabled,
  renderOption,
  onCreate,
  clearable = true,
  ...props
}: MultiComboboxProps<T, D>, ref: ForwardedRef<HTMLDivElement>) => {
  const { t } = useTranslation();
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const inputId = useUniqueId('multi-combobox-input');
  const valueItem = useMemo(() => {
    if (!value) {
      return null;
    }

    return items.filter(item => value.includes(item.value));
  }, [value, items]);

  const onEnter = (e: KeyboardEvent<HTMLInputElement>) => {
    if (!onCreate) {
      return;
    }
    const newItem = onCreate?.((e.target as HTMLInputElement).value);
    if (newItem) {
      onChange?.([...(valueItem ?? []), newItem]);
    }
    resetInputText();
  };

  const resetInputText = () => {
    if (inputRef.current) {
      inputRef.current.value = '';
    }
  };

  const handleChange = (value: ComboboxOption<T, D> | (ComboboxOption<T, D>[]) | null) => {
    onChange?.(value as ComboboxOption<T, D>[]);
    resetInputText();
  };

  return (
    <BaseCombobox.Root<ComboboxOption<T, D>, boolean>
      disabled={disabled}
      items={items}
      multiple
      value={valueItem ?? undefined}
      onValueChange={handleChange}
    >
      <InputWrapper
        {...props}
        ref={ref}
        disabled={disabled}
        placeholder={placeholder}
        rootRef={refHandler(containerRef)}
        labelProps={{ id: inputId }}
        onEnterKey={onEnter}
        suffix={(
          <>
            {clearable && <BaseCombobox.Clear
              render={(props, { disabled }) => <IconButton
                {...props}
                disabled={disabled}
                label={t('common.remove')}
                data-testid={'clear'}
                color="grey"
                variant={'text'}>
                <Icon icon={['far', 'times']} />
              </IconButton>
              } />}
            <BaseCombobox.Trigger render={(props, state) => {
              const { color, ...rest } = props;
              void color;

              return (
                <IconButton
                  data-testid={'toggle-popup'}
                  label={'Open popup'}
                  color="grey"
                  variant={'text'}
                  {...rest}
                >
                  <Icon icon={['far', state.open ? 'chevron-up' : 'chevron-down']} />
                </IconButton>
              );
            }} />
          </>
        )}>
        {({ id, input, listeners, accessibility }) => (
          <BaseCombobox.Chips ref={containerRef}>
            <TagList>
              <BaseCombobox.Value>
                {(value: ComboboxOption[]) => (
                  <>
                    {value.map(option => (
                      <BaseCombobox.Chip
                        key={option.value}
                        render={(props, state) => (
                          <Tag
                            {...props}
                            {...state}
                            suffix={(clearable || value.length > 1) && <BaseCombobox.ChipRemove
                              render={props => (<IconButton
                                {...props as IconButtonProps}
                                as={'button'}
                                data-testid={'remove'}
                                label={'Remove'}
                                variant={'text'}>
                                <Icon icon={['far', 'times']} />
                              </IconButton>)}>
                            </BaseCombobox.ChipRemove>}>
                            {option.label}
                          </Tag>
                        )}
                      >
                      </BaseCombobox.Chip>
                    ))}
                  </>
                )}
              </BaseCombobox.Value>
              <BaseCombobox.Input
                id={id}
                {...input}
                ref={inputRef}
                {...listeners}
                {...accessibility}
                placeholder={input.placeholder}
                data-testid={'multi-combobox-input'}
                className={clsx(input.className, classes.input)}
              />
            </TagList>
          </BaseCombobox.Chips>)}
      </InputWrapper>

      <BaseCombobox.Portal>
        <BaseCombobox.Positioner sideOffset={4} className={classes.positioner} anchor={containerRef}>
          <BaseCombobox.Popup className={classes.popup}>
            {onCreate ? <BaseCombobox.Empty><Typography variant='caption' dangerouslySetInnerHTML={{ __html: t('common.no-entries-but-creatable') }} /></BaseCombobox.Empty> : <BaseCombobox.Empty><Typography variant="caption" dangerouslySetInnerHTML={{ __html: t('common.no-entries') }} /></BaseCombobox.Empty>}
            <BaseCombobox.List render={<OptionGroup />}>
              {(item: ComboboxOption<T, D>) => (
                <BaseCombobox.Item
                  key={item.value}
                  value={item}
                  disabled={item.disabled}
                  render={(props, state) => {
                    const { prefix, color, ...rest } = props;
                    void prefix;
                    void color;

                    if (renderOption) {
                      return renderOption({
                        option: item,
                        state,
                        props: rest
                      });
                    }

                    return (
                      <OptionItem
                        data-testid={item.value}
                        isFocused={state.highlighted}
                        selected={state.selected}
                        disabled={state.disabled}
                        {...rest}
                      />
                    );
                  }}>
                  {item.label}
                </BaseCombobox.Item>
              )}
            </BaseCombobox.List>
          </BaseCombobox.Popup>
        </BaseCombobox.Positioner>
      </BaseCombobox.Portal>
    </BaseCombobox.Root>
  );
};

export const MultiCombobox = forwardRef(MultiComboboxInner) as <T extends number | string = string, D = unknown>(
  props: MultiComboboxProps<T, D> & { ref?: ForwardedRef<HTMLDivElement> }
) => ReturnType<typeof MultiComboboxInner>;